import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { OrderLine } from '../types';
import { SearchableSelect, Option } from './SearchableSelect';
import { productsApi } from '../services/api';

interface LineItemProps {
  line: OrderLine;
  index: number;
  onUpdate: (id: string, field: keyof OrderLine, value: string | number) => void;
  onRemove: (id: string) => void;
  onProductSearch?: (query: string) => Promise<Option[]>;
  onLineComplete?: (lineId: string) => void;
}

export const LineItem: React.FC<LineItemProps> = ({ 
  line, 
  index, 
  onUpdate, 
  onRemove,
  onProductSearch,
  onLineComplete
}) => {
  const [packagingOptions, setPackagingOptions] = useState<Option[]>([]);
  const [loadingPackaging, setLoadingPackaging] = useState(false);

  // Load packaging options when product changes
  useEffect(() => {
    const loadPackaging = async () => {
      if (!line.productId) {
        setPackagingOptions([]);
        return;
      }

      try {
        setLoadingPackaging(true);
        const product = await productsApi.getById(line.productId);
        
        if (product.availablePackaging && product.availablePackaging.length > 0) {
          const options = product.availablePackaging.map(ap => ({
            value: ap.packagingContainerId,
            label: ap.packagingContainerName,
            subLabel: ap.isDefault ? '(Predeterminado)' : undefined
          }));
          setPackagingOptions(options);

          // Auto-select default packaging if no packaging is selected
          if (!line.packagingContainerId) {
            const defaultPackaging = product.availablePackaging.find(ap => ap.isDefault);
            if (defaultPackaging) {
              onUpdate(line.id, 'packagingContainerId', defaultPackaging.packagingContainerId);
              onUpdate(line.id, 'packagingContainerName', defaultPackaging.packagingContainerName);
            }
          }
        } else {
          setPackagingOptions([]);
        }
      } catch (err) {
        console.error('Error loading packaging:', err);
        setPackagingOptions([]);
      } finally {
        setLoadingPackaging(false);
      }
    };

    loadPackaging();
  }, [line.productId]);

  const handleQuantityKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Focus packaging select
      const packagingInput = e.currentTarget.closest('.grid')?.querySelector('[data-packaging-select] input') as HTMLInputElement;
      packagingInput?.focus();
    }
  };

  const handlePackagingChange = (value: string) => {
    const selectedOption = packagingOptions.find(opt => opt.value === value);
    onUpdate(line.id, 'packagingContainerId', value);
    onUpdate(line.id, 'packagingContainerName', selectedOption?.label || '');
    
    // Trigger line complete if this is filled and has product + quantity
    if (line.productId && line.quantity > 0 && onLineComplete) {
      setTimeout(() => onLineComplete(line.id), 100);
    }
  };

  return (
    <div className="space-y-3 bg-white p-4 rounded-lg border border-green-100 hover:border-green-400 hover:shadow-md transition-all duration-200" data-line-index={index}>
      {/* Primera fila: Producto, Cantidad, Envase */}
      <div className="grid grid-cols-12 gap-3 items-end">
        
        {/* Product Select */}
        <div className="col-span-12 sm:col-span-6 md:col-span-5">
          <div className="mb-1 block sm:hidden">
            <label className="text-xs font-bold text-green-700 uppercase tracking-wider">Producto</label>
          </div>
          <SearchableSelect
            label="Producto"
            labelClassName="hidden sm:block text-xs font-bold text-green-700 uppercase tracking-wider mb-1"
            placeholder="Buscar producto..."
            value={line.productId}
            onChange={(val) => onUpdate(line.id, 'productId', val)}
            onSearch={onProductSearch}
            initialOption={line.productId && line.productName ? { value: line.productId, label: line.productName } : null}
            minSearchLength={0}
          />
        </div>

        {/* Quantity Input */}
        <div className="col-span-6 sm:col-span-3">
          <label className="block text-xs font-bold text-green-700 uppercase tracking-wider mb-1">
            Cantidad
          </label>
          <input
            type="number"
            inputMode="decimal"
            pattern="[0-9]*"
            min="1"
            step="1"
            value={line.quantity || ''}
            onChange={(e) => onUpdate(line.id, 'quantity', Number(e.target.value) || 0)}
            onKeyDown={handleQuantityKeyDown}
            className="w-full bg-green-50/30 border border-green-200 text-slate-800 text-sm rounded-md focus:ring-green-500 focus:border-green-500 block p-2.5 h-11 font-mono"
            placeholder="0"
          />
        </div>

        {/* Packaging Select */}
        <div className="col-span-4 sm:col-span-3" data-packaging-select>
          <label className="block text-xs font-bold text-green-700 uppercase tracking-wider mb-1">
            Envase
          </label>
          {loadingPackaging ? (
            <div className="w-full bg-green-50/30 border border-green-200 text-slate-400 text-sm rounded-md p-2.5 h-11 flex items-center justify-center">
              Cargando...
            </div>
          ) : packagingOptions.length > 0 ? (
            <SearchableSelect
              label=""
              placeholder="Seleccionar envase..."
              value={line.packagingContainerId || ''}
              onChange={handlePackagingChange}
              onSearch={async () => packagingOptions}
              initialOption={line.packagingContainerId && line.packagingContainerName ? { 
                value: line.packagingContainerId, 
                label: line.packagingContainerName 
              } : null}
              minSearchLength={0}
            />
          ) : (
            <div className="w-full bg-red-50 border border-red-200 text-red-600 text-xs rounded-md p-2.5 h-11 flex items-center justify-center">
              Sin envases
            </div>
          )}
        </div>

        {/* Delete Button */}
        <div className="col-span-2 sm:col-span-1 flex justify-end">
          <button
            type="button"
            onClick={() => onRemove(line.id)}
            className="h-11 w-11 flex items-center justify-center text-rose-500 hover:text-white hover:bg-rose-500 bg-rose-50 rounded-md transition-all duration-200 border border-rose-200"
            title="Eliminar línea"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Segunda fila: Observaciones */}
      <div>
        <label className="block text-xs font-bold text-green-700 uppercase tracking-wider mb-1">
          Observaciones (opcional)
        </label>
        <textarea
          value={line.observations || ''}
          onChange={(e) => onUpdate(line.id, 'observations', e.target.value)}
          placeholder="Ej. Calibre especial, corte específico, embalaje especial..."
          rows={2}
          className="w-full bg-green-50/30 border border-green-200 text-slate-800 text-sm rounded-md focus:ring-green-500 focus:border-green-500 block p-2.5 resize-none"
        />
      </div>
    </div>
  );
};
